import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { ChevronRight, Phone, ArrowRight, Camera, X, Maximize2 } from "lucide-react";
import { PHONE, PHONE_HREF } from "@/data/services";
import { GALLERY_PROJECTS as STATIC_PROJECTS } from "@/data/gallery";
import { useSiteImages } from "@/lib/siteImages";
import GoogleReviews from "@/components/GoogleReviews";

const Lightbox = ({ src, alt, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={onClose}
      data-testid="lightbox"
      className="fixed inset-0 z-[9998] bg-bg/95 backdrop-blur-sm flex items-center justify-center p-6 cursor-zoom-out"
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 w-11 h-11 flex items-center justify-center border border-line bg-bg/70 text-ink hover:text-gold hover:border-gold transition-colors"
        aria-label="Close"
        data-testid="lightbox-close"
      >
        <X size={20} />
      </button>
      <motion.img
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        src={src}
        alt={alt}
        className="max-h-[90vh] max-w-[94vw] object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </motion.div>
  );
};

const Gallery = () => {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null); // {src, alt}
  const { gallery } = useSiteImages();
  const PROJECTS = [
    ...gallery.map((g) => ({
      id: g.id,
      title: g.title || "Recent Job",
      suburb: g.suburb || "Melbourne",
      category: g.category || "Recent Work",
      scope: g.scope,
      image: g.url,
    })),
    ...STATIC_PROJECTS,
  ];
  const categories = ["All", ...Array.from(new Set(PROJECTS.map((p) => p.category)))];
  const filtered = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <div data-testid="gallery-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gold-glow opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-page px-6 lg:px-14 pt-20 pb-20">
          <nav className="text-xs tracking-widerx uppercase flex items-center gap-2 mb-12" aria-label="Breadcrumb">
            <Link to="/" className="text-muted hover:text-gold">Home</Link>
            <ChevronRight size={12} className="text-dim" />
            <span className="text-ink">Gallery</span>
          </nav>
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="eyebrow mb-6"
            >
              <span>Recent Work · Real Jobs</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display font-black text-[clamp(3rem,9vw,7.5rem)] leading-[0.9] text-ink"
            >
              Real jobs.
              <br />
              <span className="text-gold">Real work.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-8 text-muted text-lg md:text-xl max-w-xl leading-relaxed"
            >
              Every photo here is a real Styne Group job. Click any photo for full-screen.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-10 flex flex-wrap items-center gap-3"
            >
              <span className="inline-flex items-center gap-2 border border-line px-4 py-2 text-muted text-xs tracking-widerx uppercase">
                <Maximize2 size={14} className="text-gold" /> Click photo for full view
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="border-y border-line bg-elevated sticky top-20 z-30">
        <div className="mx-auto max-w-page px-6 lg:px-14 py-5 flex flex-wrap items-center gap-2">
          <span className="text-muted text-[10px] tracking-widerx uppercase mr-2">Filter</span>
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
              className={`px-4 py-2 border font-display tracking-widerx text-xs uppercase transition-colors ${
                filter === c
                  ? "border-gold text-gold bg-gold/5"
                  : "border-line text-ink hover:border-gold/40"
              }`}
            >
              {c} <span className="opacity-50 ml-1">
                {c === "All" ? PROJECTS.length : PROJECTS.filter(p => p.category === c).length}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* Projects */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-page px-6 lg:px-14 space-y-20 lg:space-y-28">
          {filtered.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7 }}
              data-testid={`gallery-project-${p.id}`}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start"
            >
              <div className={`lg:col-span-8 ${i % 2 === 1 ? "lg:col-start-5 lg:row-start-1" : ""}`}>
                <button
                  onClick={() => setLightbox({ src: p.image, alt: p.title })}
                  className="group relative block w-full aspect-[4/3] overflow-hidden border border-line cursor-zoom-in"
                  data-testid={`gallery-image-${p.id}`}
                >
                  <img
                    src={p.image}
                    alt={`${p.title} — ${p.suburb}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute bottom-4 right-4 w-10 h-10 flex items-center justify-center bg-bg/70 text-ink group-hover:text-gold transition-colors">
                    <Maximize2 size={16} />
                  </span>
                </button>
              </div>
              <div className={`lg:col-span-4 ${i % 2 === 1 ? "lg:col-start-1 lg:row-start-1" : ""}`}>
                <div className="text-gold text-xs tracking-widerx uppercase mb-3">{p.category} · {p.suburb}</div>
                <h2 className="font-display text-4xl md:text-5xl text-ink leading-[0.95] mb-6">{p.title}</h2>
                <p className="text-muted leading-relaxed mb-6">{p.scope}</p>
                <div className="pt-6 border-t border-line grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-muted text-[10px] tracking-widerx uppercase mb-1">Duration</div>
                    <div className="text-ink font-display tracking-wide text-lg">{p.duration || "—"}</div>
                  </div>
                  <div>
                    <div className="text-muted text-[10px] tracking-widerx uppercase mb-1">Project</div>
                    <div className="text-ink font-display tracking-wide text-lg">{String(i + 1).padStart(2, "0")}</div>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-24">
              <Camera size={40} className="text-muted mx-auto mb-6" />
              <p className="text-muted">No projects in this category yet — more coming.</p>
            </div>
          )}
        </div>
      </section>

      <GoogleReviews className="bg-bg" />

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-elevated border-t border-line">
        <div className="mx-auto max-w-page px-6 lg:px-14 text-center">
          <h2 className="font-display text-5xl md:text-7xl text-ink mb-6">
            Your project. <span className="text-gold">Next.</span>
          </h2>
          <p className="text-muted text-lg max-w-xl mx-auto mb-10">
            Emergency, renovation, or civil scope — happy to walk your site and give you a real quote.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href={PHONE_HREF} className="btn-gold" data-testid="gallery-cta-call">
              <Phone size={16} /> Call {PHONE}
            </a>
            <Link to="/contact" className="btn-ghost" data-testid="gallery-cta-quote">
              Request a Quote <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {lightbox && <Lightbox {...lightbox} onClose={() => setLightbox(null)} />}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
